/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";

import { DealerVinResultsRoute } from "../../../navigation/DealerRoutes";
import { useGetVehicleInfo } from "../../../queries/useGetVehicleInfo";
import { useSearchForm } from "../../../stores/hooks/useSearchForm";
import { useVehicleModelIdParam } from "../../../stores/useVehicleModelIdParam";
import { Pagination } from "../../../ui/Pagination/Pagination";
import { Section } from "../../../ui/Section/Section";
import { Spinner } from "../../../ui/Spinner";
import { NoData } from "../../NoData/NoData";
import { ContactLeadForm } from "../../ProductDetails/Common/LeadForm/ContactLeadForm";
import { useTireCompareIds } from "../TiresCompareDialog/useTireCompareIds";
import { StockLevelsModal } from "./StockLevelsModal/StockLevelsModal";
import { TireTile } from "./Tile/TireTile";
import {
  getUnavailableSeasonMessage,
  useTireResults,
} from "./useTireResults";

const placeholder = {
  filteredParts: [null, null, null, null, null, null],
  metatagList: null,
  resultParameters: null,
};

export const TireList: FunctionComponent = () => {
  const { data = placeholder } = useTireResults();
  const { t } = useTranslation();
  const { data: vehicleInfo } = useGetVehicleInfo();
  const [isVinRoute] = useRoute(DealerVinResultsRoute);
  const [, setSelectedVehicleModelId] = useVehicleModelIdParam();
  const [compareIds] = useTireCompareIds();
  const [, { setModel }] = useSearchForm();
  const decodedVehicle = vehicleInfo?.decodedVehicle;
  const modelCandidates = vehicleInfo?.modelCandidates ?? [];
  const decodedVehicleDisplay = [
    decodedVehicle?.year,
    decodedVehicle?.make,
    decodedVehicle?.matchedModelName || decodedVehicle?.model,
  ]
    .filter(Boolean)
    .join(" ");

  const selectModelCandidate = (
    vehicleModelId: number,
    vehicleModelName: string
  ): void => {
    setModel(vehicleModelName, null);
    setSelectedVehicleModelId(String(vehicleModelId));
  };

  const decodedVehicleLine = decodedVehicleDisplay ? (
    <div className="col-span-full mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-bold">{t("searchResults.decodedVehicle")}:</span>
      <span>{decodedVehicleDisplay}</span>
      {modelCandidates.length > 1 && (
        <>
          <span className="opacity-70">
            {t("searchResults.selectMatchedVehicleModel")}
          </span>
          {modelCandidates.map(({ vehicleModelId, vehicleModelName }) => (
            <button
              onClick={(): void =>
                selectModelCandidate(vehicleModelId, vehicleModelName)
              }
              className="font-bold text-primary underline-offset-2 hover:underline"
              key={vehicleModelId}
              type="button"
            >
              {vehicleModelName}
            </button>
          ))}
        </>
      )}
    </div>
  ) : null;

  if (data === null) {
    return (
      <>
        <Section className="mb-2 md:mb-4 lg:mb-6 p-4 md:p-8 font-bold">
          {t("searchResults.pricingIsNotAvailable")}
        </Section>
        <Section className="max-w-lg mx-auto bg-paper">
          <ContactLeadForm allowPricing={false} part={null} />
        </Section>
      </>
    );
  }

  if (isVinRoute) {
    return (
      <section className="tw-tire-list">
        <Section className="col-span-full mb-2 p-4">
          {decodedVehicleLine || (
            <div className="flex items-center gap-2 text-sm font-bold">
              <Spinner />
              <span>{t("searchResults.decodingVin")}</span>
            </div>
          )}
        </Section>
      </section>
    );
  }

  return (
    <section className="tw-tire-list">
      {decodedVehicleLine}
      <Pagination className="sm:col-span-2 xl:col-span-3">
        {data.filteredParts.map((product, key) => {
          return (
            <TireTile
              isCheckedForCompare={compareIds.includes(
                product?.partDetails?.primaryPart?.itemId || ""
              )}
              index={key}
              key={product?.partDetails?.primaryPart?.itemId || key}
              metatagList={data.metatagList}
              part={product}
              resultParameters={data.resultParameters}
              vehicleInfo={vehicleInfo || null}
            />
          );
        })}
      </Pagination>
      {data.filteredParts.length === 0 && (
        <NoData className="col-span-3">
          {t(
            ("unavailableSeason" in data &&
              getUnavailableSeasonMessage(data)) ||
              "searchResults.noData"
          )}
        </NoData>
      )}
      <StockLevelsModal />
    </section>
  );
};

/**
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rca.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/rfc.md
 * @see docs/worklog/TW-967-hunter-vin-results-vehicle-tire-sizes/tfc.md
 */
